import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import OnboardingLayout from "../components/layout/OnboardingLayout";
import AgentSelect from "../components/common/AgentSelect";
import Button from "../components/common/Button";
import PageLoader from "../components/common/PageLoader";
import StatusBadge from "../components/common/StatusBadge";
import { fetchAgentOnboarding, fetchCurrentAgent, submitAgentOnboarding } from "../lib/agentAuth";
import {
  ACCOUNT_TYPES,
  BANK_PROOF_DOCUMENT_TYPE,
  BUSINESS_TYPES,
  KYC_ACCEPT,
  LEGAL_STRUCTURES,
  documentStatusMeta,
  getAgentHomePath,
  getKycRequirement,
  isRejectedDocument,
  kycTypeLabel,
  latestDocumentForType,
  maskAccountNumber,
  normalizeAccountNumber,
  normalizeIfsc,
  onboardingDocumentTypes,
  parseAgentBankDetails,
  validateAgentBankDetails,
  validateKycFile,
} from "../lib/agentOnboarding";
import { agentApi } from "../lib/apiClient";
import { showApiError, showError, showErrorAction, showSuccessAction } from "../lib/alerts";
import { BRAND } from "../lib/brand";
import { fetchCities, fetchCountries, fetchStates, findLocationLabel } from "../lib/locations";

const stepLabels = [
  "Business Profile",
  "KYC Details",
  "Documents",
  "Bank Details",
  "Terms",
  "Review & Submit",
];
const LAST_STEP = stepLabels.length;

function fieldErrorClass(message) {
  return message ? " is-invalid" : "";
}

function FieldLabel({ children, required, optional }) {
  return (
    <label className="form-label fw-semibold text-sm">
      {children}
      {required ? <span className="text-danger-600"> *</span> : null}
      {optional ? <span className="text-secondary-light"> (optional)</span> : null}
    </label>
  );
}

function SubmittedIllustration() {
  return (
    <div className="agent-onboarding-art text-center mt-32 mb-8">
      <svg viewBox="0 0 560 260" role="img" aria-hidden="true" className="agent-onboarding-art__svg">
        <ellipse cx="280" cy="232" rx="168" ry="14" fill="#f3f4f6" />
        <circle cx="92" cy="78" r="38" fill="#fdecec" />
        <circle cx="468" cy="96" r="46" fill="#eef8f2" />
        <rect x="78" y="86" width="118" height="132" rx="14" fill="#fff" stroke="#ececec" strokeWidth="2" transform="rotate(-8 137 152)" />
        <rect x="96" y="112" width="72" height="8" rx="4" fill="#f3f4f6" transform="rotate(-8 132 116)" />
        <rect x="98" y="130" width="54" height="8" rx="4" fill="#f3f4f6" transform="rotate(-8 125 134)" />
        <rect x="364" y="78" width="118" height="132" rx="14" fill="#fff" stroke="#ececec" strokeWidth="2" transform="rotate(8 423 144)" />
        <rect x="386" y="104" width="72" height="8" rx="4" fill="#f3f4f6" transform="rotate(8 422 108)" />
        <rect x="390" y="122" width="54" height="8" rx="4" fill="#f3f4f6" transform="rotate(8 417 126)" />
        <rect x="208" y="48" width="144" height="168" rx="18" fill="#fff" stroke="#f0a8aa" strokeWidth="2.5" />
        <rect x="208" y="48" width="144" height="36" rx="18" fill="#fdecec" />
        <rect x="208" y="66" width="144" height="18" fill="#fdecec" />
        <rect x="232" y="104" width="96" height="8" rx="4" fill="#f8d0d1" />
        <rect x="232" y="124" width="78" height="8" rx="4" fill="#f8d0d1" />
        <rect x="232" y="144" width="88" height="8" rx="4" fill="#f8d0d1" />
        <rect x="232" y="164" width="64" height="8" rx="4" fill="#f8d0d1" />
        <circle cx="352" cy="196" r="28" fill="#45b369" />
        <path d="M341 196.5l8 8 16-18" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <p className="text-secondary small mb-0">Your documents are with Way2Umrah for verification.</p>
    </div>
  );
}
function ReviewRow({ label, value }) {
  return (
    <div className="d-flex justify-content-between gap-3 py-4">
      <span className="text-secondary small">{label}</span>
      <span className="fw-semibold text-end">{value || "Not provided"}</span>
    </div>
  );
}

function DocumentCard({
  type,
  title,
  subtitle,
  latest,
  error,
  uploading,
  progress = 0,
  onFileChange,
  onUpload,
}) {
  const status = documentStatusMeta(latest);
  const rejected = isRejectedDocument(latest);
  const approved = status.value === "approved";
  const uploadOk = Boolean(latest) && !rejected;
  const borderClass = rejected ? " border-danger" : uploadOk ? " border-success" : "";
  const percent = Math.min(100, Math.max(0, Number(progress) || 0));
  const actionLabel = uploading
    ? percent >= 100
      ? "Saving..."
      : `Uploading ${percent}%`
    : rejected
      ? "Replace Document"
      : latest
        ? "Replace file"
        : "Upload";

  return (
    <div className={`border radius-12 p-16 mb-16${borderClass}`}>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-12">
        <div>
          {subtitle ? <div className="text-uppercase text-secondary small fw-semibold mb-4">{subtitle}</div> : null}
          <div className="fw-semibold">
            {title || kycTypeLabel(type)}
            <span className="text-danger-600"> *</span>
          </div>
          {status.source ? <div className="small text-secondary mt-4">{status.source}</div> : null}
          {latest?.original_file_name ? (
            <div className={`small d-flex align-items-center gap-1 mt-4 ${approved ? "text-success-main" : rejected ? "text-danger-600" : "text-secondary"}`}>
              <iconify-icon icon={approved ? "solar:check-circle-bold" : rejected ? "solar:danger-triangle-bold" : "solar:document-outline"}></iconify-icon>
              {latest.original_file_name}
            </div>
          ) : null}
        </div>
        <StatusBadge value={status.value}>{status.label}</StatusBadge>
      </div>
      {rejected && latest?.review_notes ? (
        <div className="alert alert-danger bg-danger-100 text-danger-600 border-danger-600 border-start-width-4-px border-top-0 border-end-0 border-bottom-0 px-24 py-13 mb-12 fw-semibold text-lg radius-4">
          <div className="fw-semibold mb-4">Reason</div>
          <div className="mb-0">{latest.review_notes}</div>
        </div>
      ) : null}
      <small className="text-danger-600 d-block mb-8">{error || ""}</small>
      <div className="d-flex align-items-center gap-12">
        <input
          key={`${type}-${latest?.kyc_document_id || "empty"}`}
          type="file"
          accept={KYC_ACCEPT}
          className="form-control h-56-px bg-neutral-50 radius-12 flex-grow-1"
          disabled={Boolean(uploading)}
          onChange={(event) => onFileChange(type, event.target.files?.[0] || null)}
        />
        <Button
          type="button"
          className="btn btn-outline-primary flex-shrink-0 h-56-px px-20"
          disabled={Boolean(uploading)}
          onClick={() => onUpload(type)}
        >
          {actionLabel}
        </Button>
      </div>
      <div className="small text-secondary mt-8">PDF, JPG, or PNG. Maximum 10 MB.</div>
      {uploading ? (
        <div className="agent-upload-progress mt-12">
          <div className="agent-upload-progress-label">
            <span>{percent >= 100 ? "Saving document..." : "Uploading document..."}</span>
            <span>{percent}%</span>
          </div>
          <div className="progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}>
            <div
              className={`progress-bar${percent >= 100 ? " progress-bar-striped progress-bar-animated" : ""}`}
              style={{ width: `${Math.max(percent, 4)}%` }}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploadingType, setUploadingType] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [profile, setProfile] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [files, setFiles] = useState({});
  const [errors, setErrors] = useState({});
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [values, setValues] = useState({
    branch_name: "",
    full_name: "",
    email: "",
    mobile: "",
    business_type: "",
    country_id: "",
    state_id: "",
    city_id: "",
    address_line1: "",
    legalStructure: "",
    gst: "",
    pan: "",
    account_holder_name: "",
    account_number: "",
    confirm_account_number: "",
    ifsc_code: "",
    bank_name: "",
    bank_branch_name: "",
    account_type: "",
    terms: false,
  });

  const agencyName = values.branch_name || profile?.branch_name || profile?.full_name || "";
  const requirement = getKycRequirement(values.legalStructure);
  const profileDocuments = onboardingDocumentTypes(requirement);
  const chequeDocument = latestDocumentForType(documents, BANK_PROOF_DOCUMENT_TYPE);
  const actionRequiredDocs = documents.filter((doc) => isRejectedDocument(doc) && doc.is_current !== false);
  const verificationStatus = String(profile?.verification_status || "pending").toLowerCase();

  const wizardState = useMemo(
    () =>
      stepLabels.map((_, index) => {
        const stepNumber = index + 1;
        return {
          number: stepNumber,
          active: stepNumber === currentStep,
          activated: stepNumber < currentStep,
        };
      }),
    [currentStep]
  );

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const current = await fetchCurrentAgent();
        const home = getAgentHomePath(current);
        if (home !== "/onboarding") {
          navigate(home, { replace: true });
          return;
        }

        const onboarding = await fetchAgentOnboarding();
        if (cancelled) return;
        const nextProfile = onboarding.profile || current;
        setProfile(nextProfile);
        setDocuments(Array.isArray(onboarding.documents) ? onboarding.documents : []);
        const parsedBank = parseAgentBankDetails(nextProfile.bank_details);
        const apiBank = onboarding.bank || {};
        const bank = {
          ...parsedBank,
          ...apiBank,
          account_number: apiBank.account_number || parsedBank.account_number || "",
          branch_name: apiBank.branch_name || parsedBank.branch_name || "",
        };
        const accountNumber = bank.account_number || "";
        const nextValues = {
          branch_name: nextProfile.branch_name || "",
          full_name: nextProfile.full_name || "",
          email: nextProfile.email || "",
          mobile: nextProfile.mobile || "",
          business_type: nextProfile.business_type || "",
          country_id: nextProfile.country_id ? String(nextProfile.country_id) : "",
          state_id: nextProfile.state_id ? String(nextProfile.state_id) : "",
          city_id: nextProfile.city_id ? String(nextProfile.city_id) : "",
          address_line1: nextProfile.address_line1 || "",
          legalStructure: nextProfile.legal_structure || "",
          gst: nextProfile.gst_number || "",
          pan: nextProfile.agency_pan || "",
          account_holder_name: bank.account_holder_name || "",
          account_number: accountNumber,
          confirm_account_number: accountNumber,
          ifsc_code: bank.ifsc_code || "",
          bank_name: bank.bank_name || "",
          bank_branch_name: bank.branch_name || "",
          account_type: bank.account_type || "",
        };
        setValues((currentValues) => ({
          ...currentValues,
          ...nextValues,
        }));
        const missing = onboarding.kycSummary?.missing || [];
        const actionRequired = onboarding.kycSummary?.actionRequired || [];
        if (
          nextValues.legalStructure &&
          nextValues.pan &&
          missing.length === 0 &&
          actionRequired.length === 0 &&
          String(nextProfile.verification_status || "").toLowerCase() !== "verified"
        ) {
          setSubmitted(true);
        }
      } catch (error) {
        showApiError(error, "LOAD_ONBOARDING");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  useEffect(() => {
    fetchCountries().then(setCountries).catch(() => setCountries([]));
  }, []);

  useEffect(() => {
    if (!values.country_id) {
      setStates([]);
      return;
    }
    fetchStates(values.country_id).then(setStates).catch(() => setStates([]));
  }, [values.country_id]);

  useEffect(() => {
    if (!values.state_id) {
      setCities([]);
      return;
    }
    fetchCities(values.state_id).then(setCities).catch(() => setCities([]));
  }, [values.state_id]);

  useEffect(() => {
    if (!submitted) {
      return undefined;
    }
    const timer = window.setInterval(async () => {
      try {
        const current = await fetchCurrentAgent({ force: true });
        setProfile(current);
        if (getAgentHomePath(current) === "/packages") {
          navigate("/packages", { replace: true });
        }
      } catch (_error) {
        // Keep the submitted screen visible if a status check fails.
      }
    }, 15000);
    return () => window.clearInterval(timer);
  }, [submitted, navigate]);

  function updateValue(name, value) {
    setValues((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
  }

  function validateStep(step) {
    const nextErrors = {};
    if (step === 1) {
      ["branch_name", "full_name", "mobile", "business_type", "country_id", "state_id", "city_id", "address_line1"].forEach((field) => {
        if (!String(values[field] || "").trim()) nextErrors[field] = "This field is required.";
      });
      if (!String(values.legalStructure || "").trim()) {
        nextErrors.legalStructure = "Select a legal business structure.";
      }
    }
    if (step === 2) {
      if (!values.pan.trim()) nextErrors.pan = "PAN is required.";
      if (requirement.gstRequired && !values.gst.trim()) nextErrors.gst = "GST number is required for this legal structure.";
    }
    if (step === 3) {
      profileDocuments.forEach((type) => {
        const latest = latestDocumentForType(documents, type);
        if (!latest || isRejectedDocument(latest)) {
          nextErrors[type] = isRejectedDocument(latest)
            ? `Replace the rejected ${kycTypeLabel(type)} before continuing.`
            : `${kycTypeLabel(type)} is required.`;
        }
      });
    }
    if (step === 4) {
      Object.assign(nextErrors, validateAgentBankDetails(values));
      if (!chequeDocument || isRejectedDocument(chequeDocument)) {
        nextErrors[BANK_PROOF_DOCUMENT_TYPE] = isRejectedDocument(chequeDocument)
          ? "Replace the rejected cancelled cheque before continuing."
          : "Cancelled cheque is required as bank account proof.";
      }
    }
    if (step === 5 && !values.terms) {
      nextErrors.terms = "Confirm the declaration before continuing.";
    }
    return nextErrors;
  }

  function goNext() {
    const stepErrors = validateStep(currentStep);
    setErrors(stepErrors);
    if (Object.keys(stepErrors).length > 0) {
      showErrorAction("REQUIRED_FIELDS");
      return;
    }
    setCurrentStep((step) => Math.min(step + 1, LAST_STEP));
  }

  function goToStep(step) {
    setCurrentStep(step);
  }

  async function uploadDocument(type) {
    const file = files[type];
    const invalid = validateKycFile(file);
    if (invalid) {
      showError(invalid);
      return;
    }
    const body = new FormData();
    body.append("documentType", type);
    body.append("document", file);
    try {
      setUploadingType(type);
      setUploadProgress(1);
      await agentApi.post("/travel-partner/kyc-documents", {
        body,
        onUploadProgress: (percent) => setUploadProgress(percent),
      });
      setUploadProgress(100);
      const onboarding = await fetchAgentOnboarding();
      setDocuments(Array.isArray(onboarding.documents) ? onboarding.documents : []);
      setFiles((current) => ({ ...current, [type]: null }));
      setErrors((current) => ({ ...current, [type]: "" }));
      showSuccessAction("UPLOAD_DOCUMENT");
    } catch (error) {
      showApiError(error, "UPLOAD_DOCUMENT");
    } finally {
      setUploadingType("");
      setUploadProgress(0);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (saving) return;
    const reviewErrors = { ...validateStep(1), ...validateStep(2), ...validateStep(3), ...validateStep(4), ...validateStep(5) };
    setErrors(reviewErrors);
    if (Object.keys(reviewErrors).length > 0) {
      showErrorAction("REQUIRED_ONBOARDING");
      return;
    }

    setSaving(true);
    try {
      const result = await submitAgentOnboarding({
        branch_name: values.branch_name,
        full_name: values.full_name,
        mobile: values.mobile,
        business_type: values.business_type,
        country_id: values.country_id,
        state_id: values.state_id,
        city_id: values.city_id,
        address_line1: values.address_line1,
        legalStructure: values.legalStructure,
        gst: values.gst,
        pan: values.pan,
        terms: true,
        declaration: true,
        bank: {
          account_holder_name: values.account_holder_name,
          account_number: normalizeAccountNumber(values.account_number),
          confirm_account_number: normalizeAccountNumber(values.confirm_account_number),
          ifsc_code: normalizeIfsc(values.ifsc_code),
          bank_name: values.bank_name,
          branch_name: values.bank_branch_name,
          account_type: values.account_type,
        },
      });
      const nextProfile = result.profile || (await fetchCurrentAgent({ force: true }));
      setProfile(nextProfile);
      setDocuments(Array.isArray(result.documents) ? result.documents : documents);
      const home = getAgentHomePath(nextProfile);
      if (home === "/packages") {
        showSuccessAction("ONBOARDING_VERIFIED");
        navigate("/packages", { replace: true });
        return;
      }
      setSubmitted(true);
      showSuccessAction("SUBMIT_ONBOARDING");
    } catch (error) {
      showApiError(error, "SUBMIT_ONBOARDING");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <PageLoader fullScreen message="Loading onboarding..." />;
  }

  const selectedCountry = findLocationLabel(countries, values.country_id, "country_id", "country_name");
  const selectedState = findLocationLabel(states, values.state_id, "state_id", "state_name");
  const selectedCity = findLocationLabel(cities, values.city_id, "city_id", "city_name");
  const chequeStatus = documentStatusMeta(chequeDocument);

  async function refreshVerification() {
    try {
      const current = await fetchCurrentAgent({ force: true });
      setProfile(current);
      const home = getAgentHomePath(current);
      if (home === "/packages") {
        navigate("/packages", { replace: true });
      }
    } catch (error) {
      showApiError(error, "CHECK_VERIFICATION");
    }
  }

  if (submitted && verificationStatus !== "rejected" && actionRequiredDocs.length === 0) {
    return (
      <OnboardingLayout agencyName={agencyName}>
        <div className="card border-0 shadow-sm">
          <div className="card-body p-32 p-lg-40">
            <div className="row align-items-center g-4">
              <div className="col-lg-8">
                <div className="mb-16">
                  <StatusBadge value="Pending Verification" />
                </div>
                <h3 className="mb-12">Onboarding submitted successfully</h3>
                <p className="text-secondary mb-0">
                  Way2Umrah is verifying your agency documents. Dashboard access opens only after verification is complete. You do not need to submit again.
                </p>
              </div>
              <div className="col-lg-4 d-flex flex-wrap justify-content-lg-end gap-8">
                <Button type="button" className="btn btn-primary-600 px-32 radius-12" onClick={refreshVerification}>
                  Check verification status
                </Button>
                <Button type="button" className="btn btn-outline-secondary px-32 radius-12" onClick={() => setSubmitted(false)}>
                  Review submitted details
                </Button>
              </div>
            </div>
          </div>
        </div>
        <SubmittedIllustration />
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout agencyName={agencyName}>
      {saving ? (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ background: "rgba(255,255,255,.92)", zIndex: 30 }}>
          <div className="text-center px-3">
            <div className="spinner-border text-primary mb-3" role="status" aria-hidden="true" />
            <h5 className="mb-8">Submitting onboarding...</h5>
            <p className="mb-0 text-secondary">Please wait while we save your agency details.</p>
          </div>
        </div>
      ) : null}
      <div className="w-100">
        <div className="mb-24">
          <h3 className="mb-8">Agency onboarding</h3>
          <p className="mb-0 text-secondary-light">Your account is active. Complete KYC to access the B2B dashboard.</p>
        </div>

        {verificationStatus === "rejected" || actionRequiredDocs.length > 0 ? (
          <div className="alert alert-warning bg-warning-100 text-warning-600 border-warning-600 border-start-width-4-px border-top-0 border-end-0 border-bottom-0 px-24 py-13 mb-24 fw-semibold text-lg radius-4">
            <h6 className="mb-8">Action required</h6>
            <p className="mb-0">
              {verificationStatus === "rejected"
                ? "Verification was not approved. Update the highlighted details and resubmit."
                : "Some documents need correction. Upload replacements and resubmit."}
            </p>
          </div>
        ) : null}

        <form onSubmit={handleSubmit} noValidate>
          <div className="form-wizard-header overflow-x-auto scroll-sm pb-8 my-24 auth-wizard-steps">
            <ul className="list-unstyled form-wizard-list style-three">
              {wizardState.map((step) => (
                <li key={step.number} className={`form-wizard-list__item d-flex align-items-center gap-8 ${step.active ? "active" : ""} ${step.activated ? "activated" : ""}`}>
                  <div className="form-wizard-list__line">
                    <span className="count">{step.activated ? <i className="ri-check-line" /> : step.number}</span>
                  </div>
                  <span className="text text-xs fw-semibold">{stepLabels[step.number - 1]}</span>
                </li>
              ))}
            </ul>
          </div>

          {currentStep === 1 ? (
            <div className="card border shadow-none">
              <div className="card-body row g-3">
                {[
                  ["branch_name", "Agency name"],
                  ["full_name", "Contact person"],
                  ["email", "Email"],
                  ["mobile", "Mobile"],
                ].map(([name, label]) => (
                  <div className="col-md-6" key={name}>
                    <label className="form-label fw-semibold text-sm">{label}</label>
                    <input
                      className="form-control h-56-px bg-neutral-50 radius-12"
                      name={name}
                      value={values[name]}
                      disabled={name === "email"}
                      onChange={(event) => updateValue(name, event.target.value)}
                    />
                    <small className="text-danger-600 text-xs">{errors[name] || ""}</small>
                  </div>
                ))}
                <div className="col-md-6">
                  <label className="form-label fw-semibold text-sm">Business type</label>
                  <AgentSelect value={values.business_type} options={BUSINESS_TYPES} onChange={(value) => updateValue("business_type", value)} isSearchable={false} />
                  <small className="text-danger-600 text-xs">{errors.business_type || ""}</small>
                </div>
                <div className="col-md-6">
                  <FieldLabel required>Legal business structure</FieldLabel>
                  <AgentSelect
                    value={values.legalStructure}
                    options={LEGAL_STRUCTURES}
                    onChange={(value) => updateValue("legalStructure", value)}
                    isSearchable={false}
                  />
                  <small className="text-danger-600 text-xs">{errors.legalStructure || ""}</small>
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold text-sm">Country</label>
                  <AgentSelect
                    value={values.country_id}
                    options={countries.map((item) => ({ value: String(item.country_id), label: item.country_name }))}
                    onChange={(value) => setValues((current) => ({ ...current, country_id: value, state_id: "", city_id: "" }))}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold text-sm">State</label>
                  <AgentSelect
                    value={values.state_id}
                    options={states.map((item) => ({ value: String(item.state_id), label: item.state_name }))}
                    onChange={(value) => setValues((current) => ({ ...current, state_id: value, city_id: "" }))}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold text-sm">City</label>
                  <AgentSelect
                    value={values.city_id}
                    options={cities.map((item) => ({ value: String(item.city_id), label: item.city_name }))}
                    onChange={(value) => updateValue("city_id", value)}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label fw-semibold text-sm">Business address</label>
                  <textarea className="form-control radius-12" rows="3" value={values.address_line1} onChange={(event) => updateValue("address_line1", event.target.value)} />
                </div>
              </div>
            </div>
          ) : null}

          {currentStep === 2 ? (
            <div className="card border shadow-none">
              <div className="card-body row g-3">
                <div className="col-md-6">
                  <FieldLabel required>PAN</FieldLabel>
                  <input className={`form-control h-56-px bg-neutral-50 radius-12${fieldErrorClass(errors.pan)}`} value={values.pan} onChange={(event) => updateValue("pan", event.target.value)} />
                  <small className="text-danger-600 text-xs">{errors.pan || ""}</small>
                </div>
                <div className="col-md-6">
                  <FieldLabel required={requirement.gstRequired} optional={!requirement.gstRequired}>GST number</FieldLabel>
                  <input className="form-control h-56-px bg-neutral-50 radius-12" value={values.gst} onChange={(event) => updateValue("gst", event.target.value)} />
                  <small className="text-danger-600 text-xs">{errors.gst || ""}</small>
                </div>
              </div>
            </div>
          ) : null}

          {currentStep === 3 ? (
            <div className="card border shadow-none">
              <div className="card-body">
                <p className="text-secondary mb-16">Upload the required agency documents. Super Admin reviews each file before it is approved.</p>
                {profileDocuments.map((type) => (
                  <DocumentCard
                    key={type}
                    type={type}
                    latest={latestDocumentForType(documents, type)}
                    error={errors[type]}
                    uploading={uploadingType === type}
                    progress={uploadingType === type ? uploadProgress : 0}
                    onFileChange={(nextType, nextFile) => setFiles((current) => ({ ...current, [nextType]: nextFile }))}
                    onUpload={uploadDocument}
                  />
                ))}
              </div>
            </div>
          ) : null}

          {currentStep === 4 ? (
            <div className="card border shadow-none">
              <div className="card-body">
                <div className="row g-3 mb-24">
                  <div className="col-12 col-md-6">
                    <FieldLabel required>Account Holder Name</FieldLabel>
                    <input
                      className={`form-control h-56-px bg-neutral-50 radius-12${fieldErrorClass(errors.account_holder_name)}`}
                      autoComplete="off"
                      value={values.account_holder_name}
                      onChange={(event) => updateValue("account_holder_name", event.target.value)}
                    />
                    <small className="text-danger-600 text-xs">{errors.account_holder_name || ""}</small>
                  </div>
                  <div className="col-12 col-md-6">
                    <FieldLabel required>Account Type</FieldLabel>
                    <AgentSelect
                      value={values.account_type}
                      options={ACCOUNT_TYPES}
                      onChange={(value) => updateValue("account_type", value)}
                      isSearchable={false}
                    />
                    <small className="text-danger-600 text-xs">{errors.account_type || ""}</small>
                  </div>
                  <div className="col-12 col-md-6">
                    <FieldLabel required>Account Number</FieldLabel>
                    <input
                      className={`form-control h-56-px bg-neutral-50 radius-12${fieldErrorClass(errors.account_number)}`}
                      inputMode="numeric"
                      autoComplete="off"
                      value={values.account_number}
                      onChange={(event) => updateValue("account_number", event.target.value.replace(/[^\d]/g, ""))}
                    />
                    <small className="text-danger-600 text-xs">{errors.account_number || ""}</small>
                  </div>
                  <div className="col-12 col-md-6">
                    <FieldLabel required>Confirm Account Number</FieldLabel>
                    <input
                      className={`form-control h-56-px bg-neutral-50 radius-12${fieldErrorClass(errors.confirm_account_number)}`}
                      inputMode="numeric"
                      autoComplete="off"
                      value={values.confirm_account_number}
                      onChange={(event) => updateValue("confirm_account_number", event.target.value.replace(/[^\d]/g, ""))}
                    />
                    <small className="text-danger-600 text-xs">{errors.confirm_account_number || ""}</small>
                  </div>
                  <div className="col-12 col-md-6">
                    <FieldLabel required>IFSC Code</FieldLabel>
                    <input
                      className={`form-control h-56-px bg-neutral-50 radius-12${fieldErrorClass(errors.ifsc_code)}`}
                      autoComplete="off"
                      maxLength={11}
                      value={values.ifsc_code}
                      onChange={(event) => updateValue("ifsc_code", normalizeIfsc(event.target.value))}
                    />
                    <small className="text-danger-600 text-xs">{errors.ifsc_code || ""}</small>
                  </div>
                  <div className="col-12 col-md-6">
                    <FieldLabel required>Bank Name</FieldLabel>
                    <input
                      className={`form-control h-56-px bg-neutral-50 radius-12${fieldErrorClass(errors.bank_name)}`}
                      value={values.bank_name}
                      onChange={(event) => updateValue("bank_name", event.target.value)}
                    />
                    <small className="text-danger-600 text-xs">{errors.bank_name || ""}</small>
                  </div>
                  <div className="col-12 col-md-6">
                    <FieldLabel optional>Branch Name</FieldLabel>
                    <input
                      className="form-control h-56-px bg-neutral-50 radius-12"
                      value={values.bank_branch_name}
                      onChange={(event) => updateValue("bank_branch_name", event.target.value)}
                    />
                    <small className="text-danger-600 text-xs">{errors.bank_branch_name || ""}</small>
                  </div>
                </div>

                <DocumentCard
                  type={BANK_PROOF_DOCUMENT_TYPE}
                  title="Cancelled Cheque"
                  subtitle="Bank Account Proof"
                  latest={chequeDocument}
                  error={errors[BANK_PROOF_DOCUMENT_TYPE]}
                  uploading={uploadingType === BANK_PROOF_DOCUMENT_TYPE}
                  progress={uploadingType === BANK_PROOF_DOCUMENT_TYPE ? uploadProgress : 0}
                  onFileChange={(nextType, nextFile) => setFiles((current) => ({ ...current, [nextType]: nextFile }))}
                  onUpload={uploadDocument}
                />
              </div>
            </div>
          ) : null}

          {currentStep === 5 ? (
            <div className="card border shadow-none">
              <div className="card-body">
                <div className="form-check style-check d-flex align-items-start">
                  <input
                    className="form-check-input border border-neutral-300"
                    type="checkbox"
                    id="onboarding-terms"
                    checked={values.terms}
                    onChange={(event) => updateValue("terms", event.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="onboarding-terms">
                    I confirm that the information and documents are accurate, and I authorize Way2Umrah to verify them.
                    I also accept the{" "}
                    <a className="text-primary-600 fw-semibold text-decoration-underline" href={BRAND.termsUrl} target="_blank" rel="noopener noreferrer">
                      Terms &amp; Conditions
                    </a>{" "}
                    and{" "}
                    <a className="text-primary-600 fw-semibold text-decoration-underline" href={BRAND.privacyUrl} target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </a>
                    .
                  </label>
                </div>
                <small className="text-danger-600 text-xs">{errors.terms || ""}</small>
              </div>
            </div>
          ) : null}

          {currentStep === 6 ? (
            <div className="card border shadow-none">
              <div className="card-body">
                <div className="border radius-12 p-16 mb-16">
                  <div className="d-flex justify-content-between align-items-center mb-8">
                    <h6 className="mb-0">Business Profile</h6>
                    <button type="button" className="btn btn-link px-0" onClick={() => goToStep(1)}>Edit</button>
                  </div>
                  <div className="fw-semibold">{values.branch_name}</div>
                  <div className="text-secondary small">{values.full_name}</div>
                  <div className="text-secondary small">{values.business_type}</div>
                  <div className="text-secondary small">{values.legalStructure}</div>
                  <div className="text-secondary small">{[selectedCity, selectedState, selectedCountry].filter(Boolean).join(", ")}</div>
                  <div className="text-secondary small">{values.address_line1}</div>
                </div>

                <div className="border radius-12 p-16 mb-16">
                  <div className="d-flex justify-content-between align-items-center mb-8">
                    <h6 className="mb-0">KYC Details</h6>
                    <button type="button" className="btn btn-link px-0" onClick={() => goToStep(2)}>Edit</button>
                  </div>
                  <ReviewRow label="PAN" value={values.pan} />
                  <ReviewRow label="GST" value={values.gst} />
                </div>

                <div className="border radius-12 p-16 mb-16">
                  <div className="d-flex justify-content-between align-items-center mb-8">
                    <h6 className="mb-0">Documents</h6>
                    <button type="button" className="btn btn-link px-0" onClick={() => goToStep(3)}>Edit</button>
                  </div>
                  {profileDocuments.map((type) => {
                    const latest = latestDocumentForType(documents, type);
                    const status = documentStatusMeta(latest);
                    return (
                      <div className="d-flex justify-content-between align-items-center gap-3 py-4" key={type}>
                        <div>
                          <div className="fw-semibold">{kycTypeLabel(type)}</div>
                          {isRejectedDocument(latest) && latest?.review_notes ? (
                            <div className="small text-danger-600">Reason: {latest.review_notes}</div>
                          ) : null}
                        </div>
                        <StatusBadge value={status.value}>{status.label}</StatusBadge>
                      </div>
                    );
                  })}
                </div>

                <div className="border radius-12 p-16 mb-16">
                  <div className="d-flex justify-content-between align-items-center mb-8">
                    <h6 className="mb-0">Bank Details</h6>
                    <button type="button" className="btn btn-link px-0" onClick={() => goToStep(4)}>Edit</button>
                  </div>
                  <ReviewRow label="Account Holder Name" value={values.account_holder_name} />
                  <ReviewRow label="Account Number" value={maskAccountNumber(values.account_number)} />
                  <ReviewRow label="Bank Name" value={values.bank_name} />
                  <ReviewRow label="IFSC" value={values.ifsc_code} />
                  <ReviewRow label="Account Type" value={values.account_type} />
                  <div className="d-flex justify-content-between align-items-center gap-3 py-4">
                    <span className="text-secondary small">Cancelled Cheque</span>
                    <StatusBadge value={chequeStatus.value}>{chequeDocument ? chequeStatus.label : "Not uploaded"}</StatusBadge>
                  </div>
                </div>

                <div className="border radius-12 p-16">
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Terms</h6>
                    {values.terms ? (
                      <span className="text-success-main fw-semibold">✓ Accepted</span>
                    ) : (
                      <button type="button" className="btn btn-link px-0" onClick={() => goToStep(5)}>Edit</button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <div className="form-group d-flex flex-wrap align-items-center justify-content-end gap-8 mt-24">
            {currentStep > 1 ? (
              <Button type="button" className="btn btn-outline-secondary border-neutral-100 px-32 radius-12" onClick={() => setCurrentStep((step) => Math.max(1, step - 1))}>
                Back
              </Button>
            ) : null}
            {currentStep < LAST_STEP ? (
              <Button type="button" className="btn btn-primary-600 px-32 radius-12" onClick={goNext}>
                Next
              </Button>
            ) : (
              <Button type="submit" className="btn btn-primary-600 px-32 radius-12" disabled={saving}>
                {saving ? "Submitting..." : "Submit onboarding"}
              </Button>
            )}
          </div>
        </form>
      </div>
    </OnboardingLayout>
  );
}
